import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { mediaUrl, uploadAppImage } from '@/lib/operatorApi';

// Поле «картинка» для витрины гостевого приложения: превью + загрузка через
// /api/operator/app/uploads/:kind. Бэкенд сам ужимает и переводит в webp,
// возвращает готовый url — его и кладём в форму.

interface Props {
  kind: 'banner' | 'cuisine';
  value: string | null;
  onChange: (url: string | null) => void;
  /** Соотношение сторон превью: баннер широкий, кухня квадратная. */
  aspect?: 'wide' | 'square';
  hint?: string;
  disabled?: boolean;
}

export default function ImageUploadField({
  kind,
  value,
  onChange,
  aspect = 'wide',
  hint,
  disabled,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const res = await uploadAppImage(kind, file);
      onChange(res.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const src = mediaUrl(value);

  return (
    <div>
      <div
        className={clsx(
          'relative overflow-hidden border border-dashed border-line bg-paper',
          aspect === 'wide' ? 'aspect-[16/9]' : 'aspect-square w-40',
        )}
      >
        {src ? (
          <img src={src} alt="" className="h-full w-full object-cover" />
        ) : (
          <button
            type="button"
            onClick={pick}
            disabled={disabled || busy}
            className="grid h-full w-full place-items-center text-ink/50 transition hover:text-ink disabled:opacity-50"
          >
            <span className="flex flex-col items-center gap-2 text-[12px]">
              {busy ? (
                <Loader2 size={20} strokeWidth={1.75} className="animate-spin" />
              ) : (
                <ImagePlus size={20} strokeWidth={1.75} />
              )}
              {busy ? 'Загружаем…' : 'Выбрать файл'}
            </span>
          </button>
        )}
        {src && busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/70">
            <Loader2 size={20} strokeWidth={1.75} className="animate-spin text-ink" />
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-outline !px-3 !py-1.5 text-[12px]"
          onClick={pick}
          disabled={disabled || busy}
        >
          <ImagePlus size={13} strokeWidth={1.75} />
          {value ? 'Заменить' : 'Загрузить'}
        </button>
        {value && (
          <button
            type="button"
            className="btn-ghost !px-3 !py-1.5 text-[12px] text-red-600"
            onClick={() => onChange(null)}
            disabled={disabled || busy}
          >
            <Trash2 size={13} strokeWidth={1.75} />
            Убрать
          </button>
        )}
        {hint && <span className="text-[12px] text-ink/50">{hint}</span>}
      </div>
      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/avif,image/heic,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
